import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useDeleteBillMutation,
  useLazyGetBillByIdQuery,
  useLazyGetFilterDataQuery,
  useUpdateBillMutation,
} from '@app/api/Bills';
import type { ModelBillFilterData } from '@app/api/Bills';
import { PAYMENT_DOCUMENTS_PATH, SUCCESS } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import type { AddPaymentDocumentForm } from '@app/pages/PaymentDocuments/components/AddPaymentDocument/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

import type { BaseOptionType } from 'antd/es/select';

export const useAddPaymentDocument = () => {
  const [filters, setFilters] = useState<ModelBillFilterData | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { showNotification } = useNotification();
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const navigate = useNavigate();

  const [form] = useForm<AddPaymentDocumentForm>();

  const [searchParams] = useSearchParams();
  const documentId = Number(searchParams.get('id')) || 0;

  const [getFilterData, { isLoading: isFiltersLoading }] =
    useLazyGetFilterDataQuery();
  const [updateBill, { isLoading: isUpdateBillLoading }] =
    useUpdateBillMutation();
  const [getBillById, { isLoading: isGetBillByIdLoading }] =
    useLazyGetBillByIdQuery();
  const [deleteBill, { isLoading: isDeleteBillLoading }] =
    useDeleteBillMutation();

  const isPageLoading = isFiltersLoading || isGetBillByIdLoading;
  const isSaving = isUpdateBillLoading || isDeleteBillLoading;

  const handleSearchFilters = useCallback(async () => {
    try {
      const result = await getFilterData({ ActiveBranchId }).unwrap();
      setFilters(result);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [ActiveBranchId, getFilterData, showNotification]);

  const getDocument = useCallback(async () => {
    if (!documentId) return;

    try {
      const {
        Amount,
        Status,
        Organization,
        DocumentType,
        Comment,
        DateCreated,
      } = await getBillById({
        ActiveBranchId,
        id: documentId,
      }).unwrap();

      form.setFieldsValue({
        Amount,
        Status,
        OrganizationId: Organization?.Key,
        DocumentType,
        Comment,
        Timestamp: dayjs(DateCreated),
      });
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [ActiveBranchId, documentId, form, getBillById, showNotification]);

  const navigateToRegistry = () => {
    navigate(PAYMENT_DOCUMENTS_PATH);
  };

  const handleSaveDocument = async () => {
    const { Amount, Status, OrganizationId, DocumentType, Comment, Timestamp } =
      form.getFieldsValue();
    try {
      await updateBill({
        ActiveBranchId,
        data: {
          Id: documentId,
          Amount: Number(Amount),
          Status,
          OrganizationId: Number(OrganizationId),
          DocumentType,
          Comment,
          Timestamp: Timestamp.format('YYYY-MM-DD'),
        },
      }).unwrap();

      showNotification('', SUCCESS);
      navigateToRegistry();
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  const mapDictionaryToSelectOptions = (
    dict: Record<string, string>
  ): BaseOptionType[] =>
    Object.entries(dict).map(([value, label]) => ({
      value,
      label,
    }));

  const openDeleteDocumentModal = () => {
    setIsDeleteModalOpen(true);
  };

  const deleteDocumentHandler = async () => {
    try {
      await deleteBill({ ActiveBranchId, id: documentId }).unwrap();

      showNotification('', SUCCESS);
      navigateToRegistry();
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  return {
    form,
    handleSaveDocument,
    isPageLoading,
    handleSearchFilters,
    filters,
    mapDictionaryToSelectOptions,
    isSaving,
    getDocument,
    navigateToRegistry,
    documentId,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    openDeleteDocumentModal,
    deleteDocumentHandler,
  };
};
